import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Loader2, ArrowLeft, Mail, ChevronDown, CheckCircle2, AlertCircle,
  XCircle, KeyRound, RefreshCw, Copy,
} from "lucide-react";

const STATUS_META = {
  sent: { label: "Sent", tone: "emerald", icon: CheckCircle2 },
  failed: { label: "Failed at Resend", tone: "red", icon: XCircle },
  skipped_no_recipient: { label: "Skipped — no recipient", tone: "amber", icon: AlertCircle },
  skipped_no_api_key: { label: "Skipped — no API key", tone: "slate", icon: KeyRound },
};

const TONE_CLASSES = {
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
  red: "bg-red-50 border-red-200 text-red-800",
  amber: "bg-amber-50 border-amber-200 text-amber-900",
  slate: "bg-slate-50 border-slate-200 text-slate-700",
};

const PILL_TONE = {
  emerald: "bg-emerald-100 text-emerald-800",
  red: "bg-red-100 text-red-800",
  amber: "bg-amber-100 text-amber-900",
  slate: "bg-slate-200 text-slate-700",
};

const RANGE_OPTIONS = [
  { hours: 6, label: "Last 6 hours" },
  { hours: 24, label: "Last 24 hours" },
  { hours: 72, label: "Last 3 days" },
  { hours: 168, label: "Last 7 days" },
  { hours: 336, label: "Last 14 days" },
];

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function AdminEmailDiagnostics() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [hours, setHours] = useState(24);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true); else setRefreshing(true);
    try {
      const { data } = await api.get(`/admin/email/diagnostics?hours=${hours}&limit=100`);
      setData(data);
    } catch {
      toast.error("Couldn't load diagnostics");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [hours]);

  useEffect(() => {
    if (!user || user.role !== "platform_admin") {
      nav("/login");
      return;
    }
    load(true);
  }, [user, nav, load]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBFAF6] flex items-center justify-center" data-testid="email-diag-loading">
        <div className="text-slate-500 inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading email logs…
        </div>
      </div>
    );
  }

  const totals = data?.totals || {};
  const recent = data?.recent || [];
  const apiKeyOk = data?.resend_api_key_configured;

  return (
    <div className="min-h-screen bg-[#FBFAF6]" data-testid="email-diag-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-slate-500 hover:text-slate-700">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="w-10 h-10 rounded-xl bg-[#C75D33]/10 text-[#C75D33] flex items-center justify-center">
              <Mail className="w-5 h-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-bold text-slate-900">Email diagnostics</h1>
              <p className="text-xs text-slate-500">Every Resend send attempt, persisted</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => load(false)}
              disabled={refreshing}
              className="rounded-lg"
              data-testid="email-diag-refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${refreshing ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <div className="relative">
              <select
                value={hours}
                onChange={(e) => setHours(parseInt(e.target.value))}
                className="appearance-none pl-3 pr-9 py-2 rounded-lg border border-slate-300 bg-white text-sm font-semibold cursor-pointer focus:outline-none focus:border-[#C75D33]"
                data-testid="email-diag-range-select"
              >
                {RANGE_OPTIONS.map((o) => <option key={o.hours} value={o.hours}>{o.label}</option>)}
              </select>
              <ChevronDown className="w-4 h-4 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400" />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Environment card */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-6" data-testid="email-diag-env-card">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Resend configuration</div>
          <div className="grid sm:grid-cols-3 gap-3">
            <EnvRow
              label="API key"
              value={apiKeyOk ? "Configured" : "MISSING"}
              tone={apiKeyOk ? "emerald" : "red"}
              testid="env-api-key"
            />
            <EnvRow label="Sender" value={data?.sender_email || "—"} tone="slate" copyable testid="env-sender" />
            <EnvRow label="Reply-To" value={data?.reply_to_email || "—"} tone="slate" copyable testid="env-replyto" />
          </div>
          {data?.homeowner_sender_email && data.homeowner_sender_email !== data?.sender_email && (
            <div className="mt-3 grid sm:grid-cols-3 gap-3">
              <EnvRow label="Homeowner sender" value={data.homeowner_sender_email} tone="slate" copyable testid="env-homeowner-sender" />
            </div>
          )}
          {!apiKeyOk && (
            <div className="mt-3 rounded-xl bg-red-50 border border-red-200 p-3 text-xs text-red-800">
              <strong>Resend API key not set on this environment.</strong> Add <code>RESEND_API_KEY</code> to backend env vars
              and redeploy. Until then, every send is recorded as <em>skipped_no_api_key</em>.
            </div>
          )}
        </div>

        {/* Totals grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="email-diag-totals">
          {(["sent", "failed", "skipped_no_recipient", "skipped_no_api_key"]).map((k) => {
            const meta = STATUS_META[k];
            const Icon = meta.icon;
            return (
              <div key={k} className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`totals-${k}`}>
                <div className={`inline-flex items-center justify-center w-8 h-8 rounded-lg ${PILL_TONE[meta.tone]}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="text-3xl font-bold font-display text-slate-900 mt-3">{totals[k] || 0}</div>
                <div className="text-xs font-semibold text-slate-500 mt-0.5">{meta.label}</div>
              </div>
            );
          })}
        </div>

        {/* Recent attempts table */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200">
            <h2 className="font-display text-base font-bold text-slate-900">Recent attempts</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {recent.length} attempt{recent.length === 1 ? "" : "s"} in the last {hours}h · newest first
            </p>
          </div>
          {recent.length === 0 ? (
            <div className="px-5 py-12 text-center text-sm text-slate-500" data-testid="email-diag-empty">
              No email attempts in this range. (If you just sent a test, hit Refresh.)
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {recent.map((a) => {
                const meta = STATUS_META[a.status] || { label: a.status, tone: "slate", icon: AlertCircle };
                const Icon = meta.icon;
                return (
                  <div key={a.id} className="px-5 py-3.5" data-testid={`attempt-row-${a.id}`}>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded ${PILL_TONE[meta.tone]}`}>
                          <Icon className="w-3 h-3" /> {meta.label}
                        </span>
                        <span className="text-xs font-semibold text-slate-700 truncate">{a.kind}</span>
                      </div>
                      <span className="text-[11px] text-slate-500 font-mono">{fmtTime(a.created_at)}</span>
                    </div>
                    <div className="mt-1.5 text-sm text-slate-800 break-all">
                      <span className="text-slate-500">to </span>
                      <span className="font-mono">{a.to || "—"}</span>
                    </div>
                    <div className="mt-0.5 text-xs text-slate-500 truncate">{a.subject}</div>
                    {a.error && (
                      <div className="mt-2 rounded-lg bg-red-50 border border-red-200 px-2.5 py-1.5 text-[11px] font-mono text-red-800 break-all" data-testid={`attempt-error-${a.id}`}>
                        {a.error}
                      </div>
                    )}
                    {(a.resend_id || a.lead_id || a.company_id) && (
                      <div className="mt-2 flex flex-wrap gap-2 text-[10px] font-mono text-slate-500">
                        {a.resend_id && <span className="px-1.5 py-0.5 rounded bg-slate-100">resend_id {a.resend_id.slice(0, 12)}</span>}
                        {a.lead_id && (
                          <Link to={`/leads/${a.lead_id}`} className="px-1.5 py-0.5 rounded bg-slate-100 hover:bg-slate-200 transition-colors">
                            lead {a.lead_id.slice(0, 8)}
                          </Link>
                        )}
                        {a.company_id && <span className="px-1.5 py-0.5 rounded bg-slate-100">co {a.company_id.slice(0, 8)}</span>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function EnvRow({ label, value, tone, copyable, testid }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value || "");
      toast.success("Copied");
    } catch {}
  };
  return (
    <div className={`rounded-xl border px-3 py-2.5 ${TONE_CLASSES[tone]}`} data-testid={testid}>
      <div className="text-[10px] font-extrabold uppercase tracking-widest opacity-75">{label}</div>
      <div className="text-xs font-mono break-all mt-0.5 flex items-center gap-1.5">
        <span className="flex-1 min-w-0 truncate">{value}</span>
        {copyable && value && value !== "—" && (
          <button onClick={copy} className="opacity-50 hover:opacity-100 shrink-0" aria-label="Copy">
            <Copy className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}
